/**
 * Thread store — DB-backed via /api/thread.
 *
 * The previous version persisted to localStorage; switched so threads
 * sync across devices for the same TPO and so threads can be shared via
 * a public link. localStorage is now only used as an in-memory cache the
 * UI reads synchronously while the network round-trip is in flight (so
 * the dashboard hydrates instantly from disk, then refreshes from the
 * server).
 *
 *   listThreads()          → cached, sync (last-known list from cache)
 *   refreshThreads()       → async, fetches & updates cache
 *   loadThread(id)         → async; falls back to cache if offline
 *   saveThread(thread)     → async upsert
 *   deleteThread(id)       → async
 *   togglePin(id)          → async, returns new pinned state
 *   createShare(id, args)  → async, returns { shareToken, sharePin, shareExpiresAt }
 *   revokeShare(id)        → async, clears the share
 *   loadPublicThread(token) → async (no auth)
 *
 * Migration note: existing localStorage thread ids are preserved. On
 * first refreshThreads() the helper migrates any local-only threads to
 * the server (one POST per thread) before fetching the merged list.
 */

import { v4 as uuid } from 'uuid';

const env = (import.meta as unknown as { env: Record<string, string> }).env;
const API_BASE = env.VITE_API_URL ?? 'http://localhost:3000';
const FRONTEND_KEY = env.VITE_FRONTEND_KEY ?? '';

export interface ThreadMeta {
  id: string;
  title: string;
  updatedAt: number; // ms epoch
  pinned?: boolean;
  // Sharing — when shareToken is set, the thread is shareable via a
  // PIN-protected public link. shareExpiresAt is null for untimed shares.
  shareToken?: string | null;
  shareExpiresAt?: number | null; // ms epoch
  ownerId?: string | null;
}

export interface SerializedMessage {
  role: 'user' | 'agent';
  text?: string;
  blocks?: unknown[];
}

export interface Thread extends ThreadMeta {
  messages: SerializedMessage[];
  batchId: string | null;
  shareToken?: string | null;
}

const INDEX_KEY = 'fb-threads';
const THREAD_KEY = (id: string) => `fb-thread-${id}`;

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

function authHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  return {
    'Content-Type': 'application/json',
    ...(FRONTEND_KEY ? { 'X-Frontend-Key': FRONTEND_KEY } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Threads change frequently (pin, share, new messages) — opt out of HTTP
// caching so we never get a 304 (which fetch surfaces as `r.ok = false`,
// breaking the local-to-server sync path).
const NO_CACHE: RequestInit = { cache: 'no-store' };

export function newThreadId(): string {
  return uuid();
}

// ---------------- Cache (synchronous, last-known) ----------------

export function listThreads(): ThreadMeta[] {
  if (typeof window === 'undefined') return [];
  return safeParse<ThreadMeta[]>(localStorage.getItem(INDEX_KEY), []);
}

function writeIndexCache(list: ThreadMeta[]) {
  localStorage.setItem(INDEX_KEY, JSON.stringify(list));
}

function writeThreadCache(t: Thread) {
  localStorage.setItem(THREAD_KEY(t.id), JSON.stringify(t));
}

/** Synchronous read from cache. Returns null if not cached. */
export function loadThread(id: string): Thread | null {
  if (typeof window === 'undefined') return null;
  return safeParse<Thread | null>(localStorage.getItem(THREAD_KEY(id)), null);
}

// ---------------- Network ----------------

/**
 * Sync any local-only threads up to the server. Runs every refresh — cheap
 * because it only posts threads whose ids aren't already in the server list.
 *
 * Replaces the previous one-shot MIGRATED_KEY guard, which was unsafe: if
 * the migration ran while the API was unreachable, the guard would still
 * mark the migration "done" and the local thread would be orphaned forever.
 */
async function syncLocalToDb(serverIds: Set<string>): Promise<void> {
  if (typeof window === 'undefined') return;
  const local = listThreads();
  for (const meta of local) {
    if (serverIds.has(meta.id)) continue;
    const t = loadThread(meta.id);
    if (!t) continue;
    try {
      await fetch(`${API_BASE}/api/thread`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ id: t.id, title: t.title, messages: t.messages, batchId: t.batchId }),
      });
    } catch {/* will retry on next refresh */}
  }
}

/** Pull thread index from API, update cache, return list. */
export async function refreshThreads(): Promise<ThreadMeta[]> {
  const r = await fetch(`${API_BASE}/api/thread`, { headers: authHeaders(), ...NO_CACHE });
  if (!r.ok) return listThreads();
  type ServerThread = {
    id: string; title: string; updatedAt: string; pinned: boolean;
    shareToken: string | null; shareExpiresAt: string | null;
    ownerId: string | null;
  };
  const { threads } = await r.json() as { threads: ServerThread[] };
  const serverIds = new Set(threads.map((t) => t.id));
  const toMeta = (t: ServerThread): ThreadMeta => ({
    id: t.id,
    title: t.title,
    updatedAt: new Date(t.updatedAt).getTime(),
    pinned: t.pinned,
    shareToken: t.shareToken,
    shareExpiresAt: t.shareExpiresAt ? new Date(t.shareExpiresAt).getTime() : null,
    ownerId: t.ownerId,
  });

  // Sync any local threads the server doesn't have, then re-fetch the
  // index so the returned list reflects them.
  await syncLocalToDb(serverIds);
  const localOnly = listThreads().filter((t) => !serverIds.has(t.id));
  if (localOnly.length > 0) {
    const r2 = await fetch(`${API_BASE}/api/thread`, { headers: authHeaders(), ...NO_CACHE });
    if (r2.ok) {
      const { threads: t2 } = await r2.json() as { threads: ServerThread[] };
      const mapped2 = t2.map(toMeta);
      writeIndexCache(mapped2);
      return mapped2;
    }
  }
  const mapped = threads.map(toMeta);
  writeIndexCache(mapped);
  return mapped;
}

/** Fetch a single thread from the server; falls back to cache on error. */
export async function fetchThread(id: string): Promise<Thread | null> {
  try {
    const r = await fetch(`${API_BASE}/api/thread/${id}`, { headers: authHeaders(), ...NO_CACHE });
    if (!r.ok) return loadThread(id);
    const t = await r.json();
    const out: Thread = {
      id: t.id,
      title: t.title,
      updatedAt: new Date(t.updatedAt).getTime(),
      messages: t.messages,
      batchId: t.batchId,
      pinned: t.pinned,
      shareToken: t.shareToken,
      shareExpiresAt: t.shareExpiresAt ? new Date(t.shareExpiresAt).getTime() : null,
      ownerId: t.ownerId,
    };
    writeThreadCache(out);
    return out;
  } catch {
    return loadThread(id);
  }
}

/**
 * Probe a share link without unlocking it. Returns minimal metadata so
 * the public page can render the PIN form (or "expired" / "not found")
 * without leaking the chat content.
 */
export async function probeSharedThread(token: string): Promise<{ title: string; expired: boolean; requiresPin: boolean; shareExpiresAt: string | null } | null> {
  try {
    const r = await fetch(`${API_BASE}/api/thread/share/${token}`, {
      headers: { 'Content-Type': 'application/json', ...(FRONTEND_KEY ? { 'X-Frontend-Key': FRONTEND_KEY } : {}) },
    });
    if (!r.ok) return null;
    return r.json();
  } catch { return null; }
}

/** Submit the PIN to unlock a shared thread. Returns null on bad PIN / expiry. */
export async function unlockSharedThread(token: string, pin: string): Promise<{ id: string; title: string; messages: SerializedMessage[] } | { error: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/api/thread/share/${token}/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(FRONTEND_KEY ? { 'X-Frontend-Key': FRONTEND_KEY } : {}) },
      body: JSON.stringify({ pin }),
    });
    if (!r.ok) {
      const b = await r.json().catch(() => ({}));
      return { error: b.message || 'Could not unlock' };
    }
    const t = await r.json();
    return { id: t.id, title: t.title, messages: t.messages };
  } catch { return null; }
}

/** Upsert a thread on the server + cache. Fire-and-forget on error. */
export async function saveThread(thread: Thread): Promise<void> {
  writeThreadCache(thread);
  // Update the cached index optimistically so the UI feels instant.
  const all = listThreads();
  const prev = all.find((t) => t.id === thread.id);
  const next = all.filter((t) => t.id !== thread.id);
  next.unshift({
    id: thread.id,
    title: thread.title,
    updatedAt: thread.updatedAt,
    ...(prev?.pinned ? { pinned: true } : {}),
    ...(prev?.shareToken ? { shareToken: prev.shareToken, shareExpiresAt: prev.shareExpiresAt ?? null } : {}),
  });
  next.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || b.updatedAt - a.updatedAt);
  writeIndexCache(next);

  try {
    await fetch(`${API_BASE}/api/thread`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        id: thread.id,
        title: thread.title,
        messages: thread.messages,
        batchId: thread.batchId,
      }),
    });
  } catch {/* offline — cache will catch up on next save */}
}

/** Title-only rename. PATCHes the server and updates the cached index. */
export async function renameThread(id: string, title: string): Promise<void> {
  const trimmed = title.trim();
  if (!trimmed) return;
  // Optimistic local update
  if (typeof window !== 'undefined') {
    const all = listThreads();
    const i = all.findIndex((t) => t.id === id);
    if (i >= 0) { all[i] = { ...all[i], title: trimmed }; writeIndexCache(all); }
    const cached = loadThread(id);
    if (cached) writeThreadCache({ ...cached, title: trimmed });
    try { window.dispatchEvent(new StorageEvent('storage', { key: INDEX_KEY })); } catch {/* old browsers */}
  }
  try {
    await fetch(`${API_BASE}/api/thread/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      ...NO_CACHE,
      body: JSON.stringify({ title: trimmed }),
    });
  } catch {/* will reconcile on next refresh */}
}

export async function deleteThread(id: string): Promise<void> {
  // Optimistic local delete
  if (typeof window !== 'undefined') {
    localStorage.removeItem(THREAD_KEY(id));
    writeIndexCache(listThreads().filter((t) => t.id !== id));
  }
  try {
    await fetch(`${API_BASE}/api/thread/${id}`, { method: 'DELETE', headers: authHeaders() });
  } catch {/* will retry next refresh */}
}

export async function togglePin(id: string): Promise<boolean> {
  const doToggle = async (): Promise<Response> =>
    fetch(`${API_BASE}/api/thread/${id}/pin`, { method: 'POST', headers: authHeaders(), ...NO_CACHE });

  try {
    let r = await doToggle();
    // 404 means the thread doesn't exist on the server yet — common during
    // the localStorage→DB transition. Push the cached copy up and retry.
    if (r.status === 404) {
      const cached = loadThread(id);
      if (cached) {
        console.warn('[fb] togglePin: thread not on server, upserting from cache', id);
        await fetch(`${API_BASE}/api/thread`, {
          method: 'POST',
          headers: authHeaders(),
          ...NO_CACHE,
          body: JSON.stringify({ id: cached.id, title: cached.title, messages: cached.messages, batchId: cached.batchId }),
        });
        r = await doToggle();
      }
    }
    if (!r.ok) {
      const body = await r.text().catch(() => '');
      console.warn('[fb] togglePin failed', r.status, body);
      return listThreads().find((t) => t.id === id)?.pinned ?? false;
    }
    const { pinned } = await r.json() as { pinned: boolean };
    const all = listThreads();
    const i = all.findIndex((t) => t.id === id);
    if (i >= 0) { all[i] = { ...all[i], pinned }; writeIndexCache(all); }
    // Notify other in-app listeners (rail, dashboard's Pinned section) that
    // the cached thread index changed — `storage` events don't fire in the
    // same tab, so dispatch a synthetic one.
    try { window.dispatchEvent(new StorageEvent('storage', { key: INDEX_KEY })); } catch {/* old browsers */}
    return pinned;
  } catch (e) {
    console.warn('[fb] togglePin error', e);
    return listThreads().find((t) => t.id === id)?.pinned ?? false;
  }
}

/**
 * Create or refresh a PIN-protected share link. The server generates a
 * fresh shareToken; pin can be supplied or left to the server (auto-4-digit).
 * Re-running rotates both — invalidates any prior link/PIN combo.
 */
export async function createShare(
  id: string,
  args: { pin?: string; expiresIn: '1h' | '6h' | '1d' | null },
): Promise<{ shareToken: string; sharePin: string; shareExpiresAt: string | null }> {
  const r = await fetch(`${API_BASE}/api/thread/${id}/share`, {
    method: 'POST', headers: authHeaders(), ...NO_CACHE,
    body: JSON.stringify(args),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    throw new Error(b.message || 'Share creation failed');
  }
  const data = await r.json() as { shareToken: string; sharePin: string; shareExpiresAt: string | null };
  // Update local cache so the rail/recent list shows shared status without a refresh
  const all = listThreads();
  const i = all.findIndex((t) => t.id === id);
  if (i >= 0) {
    all[i] = {
      ...all[i],
      shareToken: data.shareToken,
      shareExpiresAt: data.shareExpiresAt ? new Date(data.shareExpiresAt).getTime() : null,
    };
    writeIndexCache(all);
    try { window.dispatchEvent(new StorageEvent('storage', { key: INDEX_KEY })); } catch {/* old browsers */}
  }
  return data;
}

/** Revoke an active share. Clears token + pin + expiry on the server. */
export async function revokeShare(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/thread/${id}/unshare`, {
    method: 'POST', headers: authHeaders(), ...NO_CACHE,
  });
  if (!r.ok) throw new Error('Could not revoke share');
  const all = listThreads();
  const i = all.findIndex((t) => t.id === id);
  if (i >= 0) {
    all[i] = { ...all[i], shareToken: null, shareExpiresAt: null };
    writeIndexCache(all);
    try { window.dispatchEvent(new StorageEvent('storage', { key: INDEX_KEY })); } catch {/* old browsers */}
  }
}

/** Format an updatedAt timestamp as a compact "2h ago" / "Apr 12". */
export function formatThreadTime(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 2 * 86_400_000) return 'yesterday';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
